import CustomButton from "components/common/Button";

import { numberToCurrencyFormat } from "util/utils";

import EdgeLogo from "components/Design/EdgeLogo/EdgeLogo";
import { FileDownloadOutlined as DownloadIcon } from "@mui/icons-material";

import "./PeruPayStubDetails.scss";

interface RenderInformationItemsProps {
  item: string;
  value: string | number;
}

interface RenderPayslipDetailsHeadingProps {
  title: string;
}

function RenderInformationItems({ item, value }: RenderInformationItemsProps) {
  const formattedValue = item.toLowerCase().includes("salary") ? `S/${value}` : value;

  return (
    <div className="peru-payroll-info-container">
      <p className="peru-payroll-info-title">{item}</p>
      <p className="peru-payroll-info-value">{formattedValue}</p>
    </div>
  );
}

interface RenderPayslipDetailsValuesProps {
  title: string;
  currentMonthAmount: number;
  ytdAmount: number;
}

interface RenderTotalValuesProps {
  value: number;
  ytd: number;
}

function RenderPayslipDetailsValues({ title, currentMonthAmount, ytdAmount }: RenderPayslipDetailsValuesProps) {
  return (
    <div className="peru-payslip-details-values">
      <p className="heading-h5-regular w-70p">{title}</p>
      <div className="flex gap-1 justify-between items-center w-45p">
        <p className="heading-h5-regular flex justify-end w-full">S/{numberToCurrencyFormat(currentMonthAmount)}</p>
        <p className="heading-h5-regular flex justify-end w-full">S/{numberToCurrencyFormat(ytdAmount)}</p>
      </div>
    </div>
  );
}

function RenderTotalValues({ value, ytd }: RenderTotalValuesProps) {
  return (
    <div className="flex gap-1 space-between">
      <div className="w-70p flex justify-end heading-h5-semibold pt-05">Total</div>
      <div className="flex justify-between pt-05 items-center gap-1 w-45p">
        <p className="flex justify-end w-full heading-h5-semibold">S/{numberToCurrencyFormat(value)}</p>
        <p className="flex justify-end w-full heading-h5-semibold">S/{numberToCurrencyFormat(ytd)}</p>
      </div>
    </div>
  );
}

function RenderPayslipDetailsHeading({ title }: RenderPayslipDetailsHeadingProps) {
  return (
    <div className="peru-payslip-details-heading">
      <div className="w-70p heading-h4-semibold">{title}</div>
      <div className="flex justify-between items-center gap-1 w-45p">
        <p className="heading-h5-regular flex justify-end w-full">Current Month</p>
        <p className="heading-h5-regular flex justify-end w-full">YTD</p>
      </div>
    </div>
  );
}

function PeruEmpSlip({ downloadPayslip, payslipData }: { downloadPayslip: () => void; payslipData: any }) {
  const {
    personalInformation,
    salaryInformation,
    benefitsInformation,
    deductionsInformation,
    incomesInformation,
    payslipMonth,
    payslipType,
    summary,
  } = payslipData || {};

  const isGratification = payslipType === "Gratification";
  const { totalIncomes, totalDeductions, netPayment } = summary?.currentMonth || {};

  const checkIsLastItem = (index: number, array: any[]) => index === array.length - 1;

  return (
    <div>
      <div className="pb-4 pt-125">
        <div className="peru-payslip-container">
          <div className="peru-payslip-header-container">
            <div className="flex">
              <div className="peru-payslip-edge-logo">
                <EdgeLogo icon size="xl" />
              </div>
              <div className="align-self-center pl-1">
                <div className="peru-payslip-title">Pay slip</div>
                <div className="peru-payslip-month">{payslipMonth}</div>
              </div>
            </div>
            <div>
              <CustomButton
                label="Download PDF"
                startIcon={<DownloadIcon />}
                variant="outlined"
                onClick={downloadPayslip}
              />
            </div>
          </div>
          <div className="peru-personal-payroll-info-container">
            <div className="w-full">
              <div className="peru-personal-payroll-info-title">Personal information</div>
              <div className="flex flex-col gap-05 w-full">
                {personalInformation?.map(({ item, value }) => (
                  <RenderInformationItems key={item} item={item} value={`${value}`} />
                ))}
              </div>
            </div>
            <div className="w-full">
              <div className="peru-personal-payroll-info-title">Payment information</div>
              <div className="flex flex-col gap-05">
                {salaryInformation?.map(({ item, value }) => (
                  <RenderInformationItems key={item + Date.now()} item={item} value={`${value}`} />
                ))}
              </div>
            </div>
          </div>
          {!isGratification && (
            <div>
              <RenderPayslipDetailsHeading title="Benefits" />
              {benefitsInformation?.map(({ item, value, ytd }, index) =>
                !checkIsLastItem(index, benefitsInformation) ? (
                  <RenderPayslipDetailsValues key={item} title={item} currentMonthAmount={value} ytdAmount={ytd} />
                ) : (
                  <RenderTotalValues key={item} value={value} ytd={ytd} />
                )
              )}
            </div>
          )}
          <div className="mt-1">
            <RenderPayslipDetailsHeading title="Incomes" />
            {incomesInformation?.map(({ item, value, ytd }, index) =>
              !checkIsLastItem(index, incomesInformation) ? (
                <RenderPayslipDetailsValues key={item} title={item} currentMonthAmount={value} ytdAmount={ytd} />
              ) : (
                <RenderTotalValues key={item} value={value} ytd={ytd} />
              )
            )}
          </div>
          <div className="mt-1">
            <RenderPayslipDetailsHeading title="Deductions" />
            {deductionsInformation?.map(({ item, value, ytd }, index) =>
              !checkIsLastItem(index, deductionsInformation) ? (
                <RenderPayslipDetailsValues key={item} title={item} currentMonthAmount={value} ytdAmount={ytd} />
              ) : (
                <RenderTotalValues key={item} value={value} ytd={ytd} />
              )
            )}
          </div>
          <div className="peru-payslip-details-footer">
            <div className="flex items-center justify-between w-full">
              <p className="heading-h4-semibold">Summary</p>
              <p className="heading-h5-regular">Current Month</p>
            </div>
            <div>
              <div className="peru-payslip-details-footer-item mt-05">
                <p className="heading-h5-regular peru-payslip-item-bb w-80p">Total incomes</p>
                <p className="heading-h5-regular peru-payslip-item-bb w-20p flex justify-end">
                  S/{numberToCurrencyFormat(totalIncomes)}
                </p>
              </div>
              <div className="peru-payslip-details-footer-item gap-1">
                <p className="heading-h5-regular peru-payslip-item-bb w-80p">Total deductions</p>
                <p className="heading-h5-regular peru-payslip-item-bb w-20p flex justify-end">
                  S/{numberToCurrencyFormat(totalDeductions)}
                </p>
              </div>
              <div className="peru-payslip-details-footer-item">
                <p className="heading-h4-semibold">Net amount</p>
                <p className="heading-h4-semibold">S/{numberToCurrencyFormat(netPayment)}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PeruEmpSlip;
